/** View state lives in the URL (deep-linkable for bots). Empty / false / null values are removed, true is "1";
 * other params (e.g. `agent=1`) are kept. */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type Patch = Record<string, string | number | boolean | null | undefined>;

export function applyPatch(sp: URLSearchParams, patch: Patch): URLSearchParams {
  const next = new URLSearchParams(sp);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === "" || v === false) next.delete(k);
    else next.set(k, v === true ? "1" : String(v));
  }
  return next;
}

export function intParam(v: string | null | undefined): number | undefined {
  if (v == null || !/^-?\d+$/.test(v)) return undefined;
  return Number(v);
}

export function flagParam(v: string | null | undefined): boolean {
  return v === "1" || v === "true";
}

export function oneOf<T extends string>(v: string | null | undefined, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly string[]).includes(v ?? "") ? (v as T) : fallback;
}

export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Patch, opts?: { replace?: boolean }) => setSp((prev) => applyPatch(prev, patch), { replace: opts?.replace }),
    [setSp],
  );
  return { sp, set };
}
